import { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { faCaretDown, faTimes } from "@fortawesome/free-solid-svg-icons";
import {
    Alert,
    Button,
    ButtonLink,
    Card,
    CardBody,
    CardHeader,
    Col,
    Content,
    ContentLoader,
    Icon,
    Modal,
    ModalTitle,
    Row,
    Table,
} from "@webtypen/react-components";
import { Page, ShiftRequestQuickHandle, Calendar } from "../components";
import { ApiHelper } from "../helpers";
import { faCheckCircle, faTimesCircle } from "@fortawesome/free-regular-svg-icons";

const OpenShiftBox = styled.div`
    .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        user-select: none;
        cursor: pointer;

        .title-content {
            flex: 1;
            font-weight: bold;
            padding: 8px 15px;
            user-select: none;

            .date {
                width: 70px;
                display: inline-block;
                opacity: 0.5;
            }

            .address {
                font-size: 11px;
                font-weight: 500;
            }
        }

        .title-options {
            padding-left: 15px;
            padding-right: 15px;

            > svg {
                margin-left: 5px;
            }
        }

        &:hover {
            background: rgba(0, 0, 0, 0.03);
            text-decoration: none;
        }

        &:active {
            background: rgba(0, 0, 0, 0.075);
            text-decoration: none;
        }
    }

    .shift-content {
        margin: 5px;
        margin-top: 1px;
        border: 1px solid #eee;
    }
`;

const HoverRow = styled.tr`
    user-select: none;
    cursor: pointer;

    &:hover {
        background: rgba(0, 0, 0, 0.03);
        text-decoration: none;
    }

    &:active {
        background: rgba(0, 0, 0, 0.075);
        text-decoration: none;
    }
`;

export const DashboardAdmin = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);
    const [openedShiftRequest, setOpenedShiftRequest] = useState(null);
    const [toggledOpenedShifts, setToggledOpenedShifts] = useState([]);

    const closeOpenedShiftRequest = () => {
        setOpenedShiftRequest(null);
    };

    const toggleOpenShift = (e) => {
        const id = e.currentTarget.getAttribute("data-id");
        if (!id || id.trim() === "") {
            return;
        }

        const newToggled = [...toggledOpenedShifts];
        if (newToggled.includes(id)) {
            newToggled.splice(newToggled.indexOf(id), 1);
        } else {
            newToggled.push(id);
        }
        setToggledOpenedShifts(newToggled);
    };

    const load = () => {
        ApiHelper.request("/api/dashboard/admin")
            .then((response) => {
                setData(response.status === "success" && response.data ? response.data : null);
                setIsLoading(false);
            })
            .catch((e) => {
                console.error(e);
                setData(null);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        setIsLoading(true);
        load();
    }, []);

    return (
        <Page title="Dashboard">
            {openedShiftRequest ? (
                <Modal
                    open={true}
                    onCloseRequest={closeOpenedShiftRequest}
                    contentAttributes={{
                        style: {
                            maxWidth: 550,
                            margin: "auto",
                            display: "flex",
                            maxHeight: "95vh",
                            flexDirection: "column",
                        },
                    }}
                >
                    <ModalTitle>
                        <Button type="button" className="float-right" onClick={closeOpenedShiftRequest} light>
                            <Icon icon={faTimes} />
                        </Button>
                        <h1>Schicht-Bewerbung ansehen</h1>
                    </ModalTitle>
                    <Content>
                        <ShiftRequestQuickHandle shiftRequestId={openedShiftRequest} onChange={load} />
                    </Content>
                </Modal>
            ) : null}

            <Content>
                {data && data.contract_requests_pending > 0 ? (
                    <Alert
                        info
                        onClick={() => navigate("/contract-request/list")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                    >
                        <div style={{ fontWeight: "bold" }}>
                            Neue {data.contract_requests_pending === 1 ? "Auftragsanfrage" : "Auftragsanfragen"}
                        </div>
                        Es gibt {data.contract_requests_pending} neue{" "}
                        {data.contract_requests_pending === 1 ? "Auftragsanfrage" : "Auftragsanfragen"}
                    </Alert>
                ) : null}

                <Row>
                    <Col width={6}>
                        <Card className="mb-3">
                            <CardHeader>Kalender</CardHeader>
                            <Calendar />
                        </Card>
                    </Col>
                    <Col width={6}></Col>
                    <Col width={6}>
                        <Card className="mb-3">
                            <CardHeader>Neue Bewerbungen</CardHeader>
                            {isLoading ? (
                                <CardBody>
                                    <ContentLoader />
                                </CardBody>
                            ) : (
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Auftrag</th>
                                            <th>Mitarbeiter</th>
                                            <th>Zeitraum</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!data || !data.new_shift_requests || data.new_shift_requests.length < 1 ? (
                                            <tr>
                                                <td colSpan={3}>
                                                    Aktuell stehen keine unbearbeiteten Bewerbungen zu Verfügung ...
                                                </td>
                                            </tr>
                                        ) : (
                                            data.new_shift_requests.map((shiftRequest, i) => (
                                                <HoverRow
                                                    key={i}
                                                    onClick={() => setOpenedShiftRequest(shiftRequest._id)}
                                                >
                                                    <td>{shiftRequest.customer_object.name}</td>
                                                    <td>
                                                        {shiftRequest.employee_object.firstname}
                                                        {shiftRequest.employee_object.lastname
                                                            ? " " + shiftRequest.employee_object.lastname
                                                            : ""}
                                                    </td>
                                                    <td>
                                                        {shiftRequest.shift_object.date ? (
                                                            <span style={{ fontWeight: "500" }}>
                                                                {moment(shiftRequest.shift_object.date).format(
                                                                    "DD.MM.YYYY"
                                                                )}
                                                                {": "}
                                                            </span>
                                                        ) : null}
                                                        {shiftRequest.shift_object.begin_planned} -{" "}
                                                        {shiftRequest.shift_object.end_planned}
                                                    </td>
                                                </HoverRow>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card>
                    </Col>

                    <Col width={6}>
                        <Card className="mb-3">
                            <CardHeader>Offene Schichten</CardHeader>
                            {isLoading ? (
                                <CardBody>
                                    <ContentLoader />
                                </CardBody>
                            ) : !data.open_shifts || data.open_shifts.length < 1 ? (
                                <CardBody style={{ userSelect: "none", opacity: 0.75 }}>
                                    Es wurden keine Schichten gefunden die noch nicht zugeteilt wurden ...
                                </CardBody>
                            ) : (
                                data.open_shifts.map((shift, i) =>
                                    !shift.contract_object || !shift.contract_object._id ? null : (
                                        <OpenShiftBox key={i}>
                                            <div
                                                className="title"
                                                onClick={toggleOpenShift}
                                                data-id={shift.contract_object._id}
                                            >
                                                <div className="title-content">
                                                    <span className="date">
                                                        {shift.contract_object && shift.contract_object.date ? (
                                                            moment(shift.contract_object.date).format("DD.MM.YY")
                                                        ) : (
                                                            <span className="text-danger">Fehlendes Datum</span>
                                                        )}
                                                    </span>
                                                    {shift.customer_object && shift.customer_object.name ? (
                                                        shift.customer_object.name
                                                    ) : (
                                                        <span className="text-danger">Unbekannter Kunde</span>
                                                    )}

                                                    {shift.contract_object &&
                                                    (shift.contract_object.address_street ||
                                                        shift.contract_object.address_housenumber ||
                                                        shift.contract_object.address_zip ||
                                                        shift.contract_object.address_city) ? (
                                                        <div className="address">
                                                            {shift.contract_object.address_street
                                                                ? shift.contract_object.address_street + " "
                                                                : ""}
                                                            {shift.contract_object.address_housenumber}
                                                            {shift.contract_object.address_street ||
                                                            shift.contract_object.address_housenumber
                                                                ? " | "
                                                                : ""}
                                                            {shift.contract_object.address_zip
                                                                ? shift.contract_object.address_zip + " "
                                                                : shift.contract_object.address_zip}
                                                            {shift.contract_object.address_city}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="title-options">
                                                    {shift.entries
                                                        ? shift.entries.length === "1"
                                                            ? "Eine Bewerbung"
                                                            : shift.entries.length + " Bewerbungen"
                                                        : "0"}

                                                    <Icon icon={faCaretDown} />
                                                </div>
                                            </div>

                                            {toggledOpenedShifts &&
                                            toggledOpenedShifts.includes(shift.contract_object._id) ? (
                                                !shift.entries || shift.entries.length < 1 ? (
                                                    <div className="pt-3 pb-3 pl-3 pr-3">
                                                        Keine Schichten verfügbar ...
                                                    </div>
                                                ) : (
                                                    <div className="shift-content">
                                                        <Table>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: 80 }}>Datum</th>
                                                                    <th style={{ width: 80 }}>Von</th>
                                                                    <th style={{ width: 80 }}>Bis</th>
                                                                    <th style={{ width: 80 }} className="text-center">
                                                                        Frei?
                                                                    </th>
                                                                    <th>Rolle / Typ</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {shift.entries.map((entry, e) => (
                                                                    <tr key={e}>
                                                                        <td>
                                                                            {entry.date
                                                                                ? moment(entry.date).format("DD.MM.")
                                                                                : "-"}
                                                                        </td>
                                                                        <td>
                                                                            {entry.begin_planned
                                                                                ? entry.begin_planned
                                                                                : "-"}
                                                                        </td>
                                                                        <td>
                                                                            {entry.end_planned
                                                                                ? entry.end_planned
                                                                                : "-"}
                                                                        </td>
                                                                        <td className="text-center">
                                                                            {entry.is_free ? (
                                                                                <Icon icon={faCheckCircle} />
                                                                            ) : (
                                                                                <Icon
                                                                                    style={{ opacity: 0.5 }}
                                                                                    icon={faTimesCircle}
                                                                                />
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {entry.type_object && entry.type_object.name
                                                                                ? entry.type_object.name
                                                                                : "-"}
                                                                        </td>
                                                                        <td className="text-right">
                                                                            <ButtonLink
                                                                                to={
                                                                                    "/contracts/" +
                                                                                    shift.contract_object._id +
                                                                                    "/edit"
                                                                                }
                                                                                xs
                                                                                light
                                                                            >
                                                                                Öffnen
                                                                            </ButtonLink>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                )
                                            ) : null}
                                        </OpenShiftBox>
                                    )
                                )
                            )}
                        </Card>
                    </Col>
                </Row>
            </Content>
        </Page>
    );
};
